// Pure state machine for a confirmation-card batch. Kept framework-free so the
// transitions (pending → applying → applied/failed, → rejected, → expired) are
// unit-testable without React. The panel holds a BatchState per card and calls
// these transitions; rendering is a function of `status`.

import type { BatchEvent, BatchState, ActionResult } from './types.ts';

/**
 * Parse the contract's open-ended `expires_at` into epoch-ms.
 * Accepts epoch-ms (number or numeric string) or an ISO-8601 string.
 * Returns null when absent/unparseable ⇒ "no client-side expiry".
 */
export function parseExpiry(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // Bare integer string ⇒ epoch ms.
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const ms = Date.parse(trimmed);
  return Number.isNaN(ms) ? null : ms;
}

/** Build the initial pending state from a batch event. */
export function initBatch(ev: BatchEvent, now = Date.now()): BatchState {
  const expiresAt = parseExpiry(ev.expires_at);
  const expired = expiresAt !== null && expiresAt <= now;
  return {
    batch_id: ev.batch_id,
    title: ev.title,
    actions: ev.actions ?? [],
    expiresAt,
    status: expired ? 'expired' : 'pending',
  };
}

export function isExpired(b: BatchState, now = Date.now()): boolean {
  return b.expiresAt !== null && b.expiresAt <= now;
}

/** Only a live, pending batch can be acted on. */
export function canApply(b: BatchState, now = Date.now()): boolean {
  return b.status === 'pending' && !isExpired(b, now);
}

/** Recompute a pending batch's status against the clock (call on a timer/tick). */
export function tick(b: BatchState, now = Date.now()): BatchState {
  if (b.status === 'pending' && isExpired(b, now)) return { ...b, status: 'expired' };
  return b;
}

export function toApplying(b: BatchState): BatchState {
  return { ...b, status: 'applying', error: undefined };
}

export function toRejected(b: BatchState): BatchState {
  return { ...b, status: 'rejected' };
}

/** Resolve an apply attempt from the confirm response. Any failed action marks
 *  the batch 'failed' but still carries the per-action results for display. */
export function toResolved(b: BatchState, results: ActionResult[]): BatchState {
  const anyFailed = results.some((r) => !r.ok);
  return { ...b, status: anyFailed ? 'failed' : 'applied', results };
}

/** Apply threw (network/500) before we got per-action results. */
export function toFailed(b: BatchState, message: string): BatchState {
  return { ...b, status: 'failed', error: message };
}
